import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Pencil, Trash2, Gauge, MapPin, Calendar, IdCard, Fuel,
  Users, Wrench, Ban, Plus, User, FileDown, History, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { VehicleForm } from "../components/VehicleForm";
import { VehicleMap } from "../components/VehicleMap";
import { FuelSection } from "../components/FuelSection";
import { DocumentsSection } from "../components/DocumentsSection";
import { AlertBadge } from "../components/AlertBadge";
import { vidangeLevel } from "../lib/alerts";
import { useAuth } from "../context/AuthContext";

const Cell = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`nb-card p-5 ${className}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} strokeWidth={2.5} className="text-[#4F46E5]" />
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
    </div>
    <p className="font-heading font-bold text-lg break-words">{value || "—"}</p>
  </div>
);

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user && (user.role === "admin" || user.role === "editor");
  const [v, setV] = useState(null);
  const [editing, setEditing] = useState(false);
  const [indispo, setIndispo] = useState({ debut: "", fin: "", raison: "" });
  const [hist, setHist] = useState({ type: "vidange", date: "", km: "", note: "" });

  const load = async () => {
    const { data } = await api.get(`/vehicles/${id}`);
    setV(data);
  };

  useEffect(() => {
    load();
  }, [id]);

  const update = async (payload) => {
    await api.put(`/vehicles/${id}`, payload);
    await load();
    toast.success("Véhicule mis à jour");
  };

  const remove = async () => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Véhicule supprimé");
    navigate("/vehicules");
  };

  const addIndispo = async (e) => {
    e.preventDefault();
    if (!indispo.debut || !indispo.raison) {
      toast.error("Date de début et raison requises");
      return;
    }
    await api.post(`/vehicles/${id}/indisponibilites`, indispo);
    setIndispo({ debut: "", fin: "", raison: "" });
    await load();
    toast.success("Indisponibilité ajoutée");
  };

  const delIndispo = async (iid) => {
    await api.delete(`/vehicles/${id}/indisponibilites/${iid}`);
    await load();
  };

  const addHist = async (e) => {
    e.preventDefault();
    if (!hist.date) {
      toast.error("Date requise");
      return;
    }
    await api.post(`/vehicles/${id}/historique`, { ...hist, km: Number(hist.km) || 0 });
    setHist({ type: "vidange", date: "", km: "", note: "" });
    await load();
    toast.success("Entrée ajoutée à l'historique");
  };

  const delHist = async (hid) => {
    await api.delete(`/vehicles/${id}/historique/${hid}`);
    await load();
  };

  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/vehicles/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiche_${v.immatriculation}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fiche PDF téléchargée");
    } catch {
      toast.error("Échec de l'export PDF");
    }
  };

  if (!v) return <div className="ml-64 p-8">Chargement...</div>;

  const vLevel = vidangeLevel(v.vidange_km_restant);
  const inputCls = "nb-input w-full px-3 py-2.5 font-medium text-sm";

  return (
    <div className="ml-64 p-8 min-h-screen">
      <button
        data-testid="back-button"
        onClick={() => navigate("/vehicules")}
        className="flex items-center gap-2 font-bold text-sm mb-6 hover:text-[#4F46E5] transition-colors"
      >
        <ArrowLeft strokeWidth={2.5} size={18} /> Retour à la liste
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 mc-slide-up">
        <div className="flex items-center gap-4">
          <span className="font-mono-plate font-bold bg-white border-2 border-slate-900 rounded-lg px-4 py-2.5 text-xl uppercase relative pl-9 nb-shadow">
            <span className="absolute left-0 top-0 bottom-0 w-5 bg-[#4F46E5] rounded-l-md" />
            {v.immatriculation}
          </span>
          <span
            data-testid="vehicle-status"
            className={`text-sm font-bold px-3 py-1.5 rounded-lg border-2 border-slate-900 ${
              v.statut === "indisponible" ? "bg-[#EF4444] text-white" : "bg-[#4ADE80]"
            }`}
          >
            {v.statut === "indisponible" ? "Indisponible" : "Disponible"}
          </span>
        </div>
        <div className="flex gap-3">
          <button data-testid="export-pdf-button" onClick={exportPdf} className="flex items-center gap-2 bg-white font-bold px-5 py-2.5 rounded-xl nb-btn">
            <FileDown strokeWidth={2.5} size={18} /> PDF
          </button>
          {canWrite && (
            <>
              <button data-testid="edit-vehicle-button" onClick={() => setEditing(true)} className="flex items-center gap-2 bg-white font-bold px-5 py-2.5 rounded-xl nb-btn">
                <Pencil strokeWidth={2.5} size={18} /> Modifier
              </button>
              <button data-testid="delete-vehicle-button" onClick={remove} className="flex items-center gap-2 bg-[#EF4444] text-white font-bold px-5 py-2.5 rounded-xl nb-btn">
                <Trash2 strokeWidth={2.5} size={18} /> Supprimer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Alerts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="nb-card p-5 mc-pop">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contrôle technique · 6 mois</p>
          <AlertBadge dateStr={v.controle_6mois_date} label="CT 6 mois" testid="detail-ct-6mois" />
          <p className="text-sm text-slate-500 font-medium mt-2">{v.controle_6mois_date || "Non planifié"}</p>
        </div>
        <div className="nb-card p-5 mc-pop">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contrôle technique · annuel</p>
          <AlertBadge dateStr={v.controle_annuel_date} label="CT annuel" testid="detail-ct-annuel" />
          <p className="text-sm text-slate-500 font-medium mt-2">{v.controle_annuel_date || "Non planifié"}</p>
        </div>
        <div className="nb-card p-5 mc-pop">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Vidange</p>
          <div
            data-testid="detail-vidange"
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-slate-900 font-bold text-xs ${
              vLevel === "green" ? "bg-[#4ADE80]" : vLevel === "orange" ? "bg-[#FB923C]" : vLevel === "red" ? "bg-[#EF4444] text-white" : "bg-slate-100"
            }`}
          >
            <Wrench size={14} strokeWidth={2.5} />
            {v.vidange_km_restant > 0 ? `Dans ${v.vidange_km_restant.toLocaleString("fr-FR")} km` : "À faire !"}
          </div>
          <p className="text-sm text-slate-500 font-medium mt-2">Prochaine à {v.vidange_km_prochain?.toLocaleString("fr-FR")} km</p>
        </div>
      </div>

      {/* Bento info grid */}
      <h2 className="font-heading font-black text-2xl mb-4">Fiche véhicule</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Cell icon={User} label="Conducteur" value={v.conducteur} />
        <Cell icon={Calendar} label="1ère mise en circulation" value={v.date_premiere_circulation} />
        <Cell icon={IdCard} label="N° licence" value={v.numero_licence} />
        <Cell icon={Gauge} label="Kilométrage" value={v.kilometrage ? `${v.kilometrage.toLocaleString("fr-FR")} km` : "—"} />
        <Cell icon={Fuel} label="Motorisation" value={v.motorisation} />
        <Cell
          icon={MapPin}
          label="Géolocalisation"
          value={v.geoloc_equipe ? `${v.geoloc_modele || "Boîtier GPS"}${v.geoloc_position ? ` · ${v.geoloc_position}` : ""}` : "Non équipé"}
        />
        <Cell
          icon={CreditCard}
          label="Carte carburant / recharge"
          value={
            v.carte_type && v.carte_type !== "aucune"
              ? `${{ gasoil: "Gasoil", electrique: "Électrique", les_deux: "Gasoil + Électrique" }[v.carte_type]}${v.carte_numero ? ` · ${v.carte_numero}` : ""}${(v.carte_conducteur || v.conducteur) ? ` · ${v.carte_conducteur || v.conducteur}` : ""}`
              : "Aucune"
          }
        />
      </div>

      {v.geoloc_equipe && v.latitude != null && v.longitude != null && (
        <div className="nb-card p-6 mb-8 mc-pop" data-testid="map-section">
          <div className="flex items-center gap-2 mb-4">
            <MapPin size={20} strokeWidth={2.5} className="text-[#4F46E5]" />
            <h3 className="font-heading font-black text-xl">Position sur la carte</h3>
          </div>
          <VehicleMap lat={Number(v.latitude)} lng={Number(v.longitude)} label={`${v.immatriculation} — ${v.conducteur || ""}`} />
        </div>
      )}

      {/* Binôme */}
      <div className="nb-card p-6 mb-8 mc-pop">
        <div className="flex items-center gap-2 mb-4">
          <Users size={20} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h3 className="font-heading font-black text-xl">Suivi du binôme</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Début</span>
            <p className="font-heading font-bold text-lg">{v.binome_debut || "—"}</p>
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Fin</span>
            <p className="font-heading font-bold text-lg">{v.binome_fin || "—"}</p>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-slate-50 border-2 border-slate-200">
          <p className="text-sm font-medium whitespace-pre-wrap">{v.binome_suivi || "Aucune note de suivi."}</p>
        </div>
      </div>

      {/* Indisponibilités */}
      <div className="nb-card p-6 mc-pop">
        <div className="flex items-center gap-2 mb-4">
          <Ban size={20} strokeWidth={2.5} className="text-[#EF4444]" />
          <h3 className="font-heading font-black text-xl">Fiche d'indisponibilité</h3>
        </div>

        {canWrite && <form onSubmit={addIndispo} className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end mb-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Début</label>
            <input type="date" data-testid="indispo-debut" className={inputCls} value={indispo.debut} onChange={(e) => setIndispo((p) => ({ ...p, debut: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Fin</label>
            <input type="date" data-testid="indispo-fin" className={inputCls} value={indispo.fin} onChange={(e) => setIndispo((p) => ({ ...p, fin: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Raison</label>
            <input data-testid="indispo-raison" className={inputCls} value={indispo.raison} onChange={(e) => setIndispo((p) => ({ ...p, raison: e.target.value }))} placeholder="Panne, entretien..." />
          </div>
          <button type="submit" data-testid="indispo-add" className="bg-[#4F46E5] text-white font-bold px-4 py-2.5 rounded-xl nb-btn flex items-center gap-1">
            <Plus strokeWidth={2.5} size={18} /> Ajouter
          </button>
        </form>}

        {(!v.indisponibilites || v.indisponibilites.length === 0) ? (
          <p className="text-slate-400 font-medium text-sm py-2">Aucune indisponibilité enregistrée.</p>
        ) : (
          <div className="space-y-2">
            {v.indisponibilites.map((it) => (
              <div key={it.id} data-testid={`indispo-item-${it.id}`} className="flex items-center justify-between p-3 rounded-xl border-2 border-slate-900 bg-white">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-mono-plate font-bold">{it.debut}{it.fin ? ` → ${it.fin}` : " → en cours"}</span>
                  <span className="font-semibold">{it.raison}</span>
                </div>
                {canWrite && <button onClick={() => delIndispo(it.id)} data-testid={`indispo-del-${it.id}`} className="text-red-600 hover:scale-110 transition-transform">
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historique entretien */}
      <div className="nb-card p-6 mt-8 mc-pop" data-testid="historique-section">
        <div className="flex items-center gap-2 mb-4">
          <History size={20} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h3 className="font-heading font-black text-xl">Historique entretien</h3>
        </div>

        {canWrite && <form onSubmit={addHist} className="grid sm:grid-cols-[1.3fr_1fr_1fr_2fr_auto] gap-3 items-end mb-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Type</label>
            <select data-testid="hist-type" className={inputCls} value={hist.type} onChange={(e) => setHist((p) => ({ ...p, type: e.target.value }))}>
              <option value="vidange">Vidange</option>
              <option value="controle_6mois">Contrôle 6 mois</option>
              <option value="controle_annuel">Contrôle annuel</option>
              <option value="autre">Autre entretien</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Date</label>
            <input type="date" data-testid="hist-date" className={inputCls} value={hist.date} onChange={(e) => setHist((p) => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Km</label>
            <input type="number" data-testid="hist-km" className={inputCls} value={hist.km} onChange={(e) => setHist((p) => ({ ...p, km: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Note</label>
            <input data-testid="hist-note" className={inputCls} value={hist.note} onChange={(e) => setHist((p) => ({ ...p, note: e.target.value }))} placeholder="Détails..." />
          </div>
          <button type="submit" data-testid="hist-add" className="bg-[#4F46E5] text-white font-bold px-4 py-2.5 rounded-xl nb-btn flex items-center gap-1">
            <Plus strokeWidth={2.5} size={18} /> Ajouter
          </button>
        </form>}

        {(!v.historique || v.historique.length === 0) ? (
          <p className="text-slate-400 font-medium text-sm py-2">Aucun entretien enregistré.</p>
        ) : (
          <div className="space-y-2">
            {[...v.historique].sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((h) => {
              const typeLabel = { vidange: "Vidange", controle_6mois: "Contrôle 6 mois", controle_annuel: "Contrôle annuel", autre: "Entretien" }[h.type] || h.type;
              return (
                <div key={h.id} data-testid={`hist-item-${h.id}`} className="flex items-center justify-between p-3 rounded-xl border-2 border-slate-900 bg-white">
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <span className="font-bold px-2 py-0.5 rounded-lg border-2 border-slate-900 bg-[#FBBF24] text-xs">{typeLabel}</span>
                    <span className="font-mono-plate font-bold">{h.date}</span>
                    {h.km ? <span className="text-slate-600 font-medium">{Number(h.km).toLocaleString("fr-FR")} km</span> : null}
                    {h.note ? <span className="font-medium text-slate-700">— {h.note}</span> : null}
                  </div>
                  {canWrite && <button onClick={() => delHist(h.id)} data-testid={`hist-del-${h.id}`} className="text-red-600 hover:scale-110 transition-transform">
                    <Trash2 size={18} strokeWidth={2.5} />
                  </button>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fuel + Documents */}
      <FuelSection vehicleId={id} canWrite={canWrite} />
      <DocumentsSection vehicleId={id} canWrite={canWrite} />

      {editing && <VehicleForm initial={v} onSubmit={update} onClose={() => setEditing(false)} />}
    </div>
  );
}
